import { supportedTypes } from '@remote-com/mushu/src/components/Form/DynamicForm/helpers';
import {
  baseString,
  emailSchema,
  validateIsNumber,
  validateIsNumberOptional,
  genericRequiredLabel,
} from '@remote-com/mushu/src/helpers/validationSchema';
import he from 'he';
import { FormLabel } from 'panda/src/views/ReimagineRemote/styles';
import { listToOptions } from 'remote-com-shared/components/ModalForm/helpers';
import { getAllCountries, reasonOptions } from 'remote-com-shared/data';
import { boolean, number } from 'yup';

import { defaultLocale } from '../locales/config';

import { HubSpotRichText } from './HubSpotRichText';

export const mobileNumberValidation = baseString
  .max(30, 'Must be at most 30 digits')
  .matches(
    /^(\+|00)\d*$/,
    'Please insert only the country code and phone number, without letters or spaces. e.g. +11234567890'
  );

function parseLabel(label) {
  return he.decode(label);
}

export function renderHiddenField(field) {
  const { name, defaultValue } = field;
  return {
    type: supportedTypes.HIDDEN,
    name,
    value: defaultValue,
    visibilityCondition: () => false,
  };
}

export function renderTextField(field) {
  const { name, label, required, fieldType, unselectedLabel, placeholder, description } = field;
  const schema = name === 'email' ? emailSchema : baseString;
  let type = name === 'email' ? supportedTypes.EMAIL : supportedTypes.TEXT;
  if (fieldType === 'textarea') {
    type = supportedTypes.TEXTAREA;
  }
  if (fieldType === 'phonenumber') {
    type = supportedTypes.TEL;
  }

  return {
    type,
    name,
    label: parseLabel(label),
    placeholder: parseLabel(unselectedLabel || placeholder || label),
    description,
    required,
    schema: required ? schema.required(genericRequiredLabel) : schema,
  };
}

export function renderNumberField(field) {
  const { name, label, required, fieldType, unselectedLabel, placeholder, description } = field;
  const schema = number().typeError('The value must be a number');

  const type = fieldType;

  return {
    type,
    name,
    label: parseLabel(label),
    placeholder: parseLabel(unselectedLabel || placeholder || label),
    description,
    required,
    schema: required ? schema.required(genericRequiredLabel) : schema,
  };
}

export function renderSelectField(field) {
  const { label, required, options, name, fieldType, unselectedLabel, placeholder, description } =
    field;
  const schema = baseString;
  const isMulti = fieldType === 'checkbox';

  const showLabelForSpecificNames = [
    'do_you_already_have_an_entity_in_your_target_countries_',
    'company_headquarters',
  ];

  return {
    type: supportedTypes.SELECT,
    name,
    label: parseLabel(label),
    required,
    options,
    displayLabel: isMulti || showLabelForSpecificNames.includes(field.name),
    placeholder: parseLabel(unselectedLabel || placeholder || label),
    description,
    isMulti,
    ...(isMulti
      ? {
          transformValue: (option) => {
            if (Array.isArray(option)) {
              return option.map((optn) => optn?.value).join(';');
            }
            return option?.value;
          },
        }
      : { transformValue: (option) => option?.value }),
    schema: required ? schema.required(genericRequiredLabel) : schema,
  };
}

export function renderRadioField(field) {
  const { label, required, options, name, description } = field;
  const schema = baseString;

  return {
    type: supportedTypes.RADIO,
    direction: 'column',
    name,
    label: parseLabel(label),
    required,
    options,
    description,
    schema: required ? schema.required(genericRequiredLabel) : schema,
  };
}

export function renderBooleanCheckbox(field) {
  const { label, required, name } = field;
  return {
    type: supportedTypes.ACK_CHECK,
    schema: boolean(),
    required,
    name,
    // eslint-disable-next-line react/no-danger
    label: <FormLabel dangerouslySetInnerHTML={{ __html: label }} />,

    defaultValue: true,
  };
}

export function renderRichText(richText) {
  return {
    type: supportedTypes.EXTRA,
    Component() {
      return <HubSpotRichText {...richText} />;
    },
  };
}

export function renderSelectAsTextField(field) {
  const { name, label, required, unselectedLabel, placeholder } = field;
  return {
    type: supportedTypes.TEXT,
    name,
    label: parseLabel(label),
    required,
    placeholder: parseLabel(unselectedLabel || placeholder || label),
    schema: required ? required(genericRequiredLabel) : baseString,
  };
}

/**
 * Modify fields for HubSpot fields such as `target_countries`, `promo_code`, etc.
 * @param {*} field
 */
export function handleSpecificHubSpotFields(field) {
  const { name, label, unselectedLabel, placeholder, required, locale, options } = field;
  switch (name) {
    case 'location': {
      return {
        type: supportedTypes.SELECT,
        name,
        label: parseLabel(label),
        placeholder: parseLabel(unselectedLabel || placeholder || label),
        isControlled: true,
        required,
        options: !locale || locale === defaultLocale ? listToOptions(getAllCountries()) : options,
        transformValue: (option) => option.label,
        schema: required ? baseString.required(genericRequiredLabel) : baseString,
      };
    }

    case 'promo_code': {
      return {
        type: supportedTypes.TEXT,
        name,
        label,
        placeholder,
        required,
        schema: baseString,
      };
    }

    case 'reason': {
      return {
        type: supportedTypes.SELECT,
        name,
        label: parseLabel(label),
        placeholder: parseLabel(unselectedLabel || placeholder || label),
        isControlled: true,
        required: true,
        options: listToOptions(reasonOptions),
        transformValue: (option) => option.label,
        schema: required ? baseString.required(genericRequiredLabel) : baseString,
      };
    }

    case 'employees_to_onboard': {
      return {
        ...field,
        type: supportedTypes.NUMBER,

        schema: required
          ? validateIsNumber.required(genericRequiredLabel)
          : validateIsNumberOptional,
      };
    }

    case 'employee_count': {
      return {
        type: supportedTypes.NUMBER,
        name,
        required,
        label,
        placeholder,
        schema: required
          ? validateIsNumber.required(genericRequiredLabel)
          : validateIsNumberOptional,
      };
    }

    case 'phone': {
      return {
        type: supportedTypes.TEL,
        name,
        required,
        label,
        displayLabel: true,
        placeholder: '+1234567890',
        schema: required
          ? mobileNumberValidation.required(genericRequiredLabel)
          : mobileNumberValidation,
      };
    }

    default:
      break;
  }

  return field;
}

export function getField(field, { parentField, filters = [] } = {}) {
  const { fieldType } = field;

  let formField = null;
  switch (fieldType) {
    case 'text':
    case 'phonenumber': {
      formField = renderTextField(field);
      break;
    }
    case 'number': {
      formField = renderNumberField(field);
      break;
    }
    case 'textarea': {
      formField = renderTextField(field);
      break;
    }

    case 'select': {
      formField = renderSelectField(field);
      break;
    }

    case 'checkbox': {
      formField = renderSelectField(field);
      break;
    }

    case 'booleancheckbox': {
      formField = renderBooleanCheckbox(field);
      break;
    }

    case 'radio': {
      formField = renderRadioField(field);
      break;
    }

    default:
      throw new Error(`Unsupported ${fieldType} in getField`);
  }

  if (formField && parentField) {
    switch (parentField.fieldType) {
      case 'select':
      case 'checkbox': {
        if (filters.length > 0) {
          formField.visibilityCondition = (formValues) => {
            const parentValue = formValues[parentField.name];

            const matchesFilters = filters.filter(({ strValues }) =>
              strValues.includes(parentValue)
            );

            return matchesFilters.length > 0;
          };
        }
        break;
      }
      default:
        break;
    }
  }

  return formField;
}

export function getFields(formFieldGroups) {
  return formFieldGroups.reduce((fieldsList, fieldGroup) => {
    if (fieldGroup.richText.content) {
      fieldsList.push(renderRichText(fieldGroup.richText));
    }

    fieldGroup.fields.forEach((field) => {
      const { hidden, dependentFieldFilters } = field;

      if (hidden) {
        fieldsList.push(renderHiddenField(field));
        return;
      }

      fieldsList.push(getField(field));

      if (dependentFieldFilters.length > 0) {
        dependentFieldFilters.forEach(({ filters, dependentFormField }) => {
          const formField = getField(dependentFormField, { parentField: field, filters });
          if (formField) {
            fieldsList.push(formField);
          }
        });
      }
    });

    return fieldsList;
  }, []);
}
